import React, { Suspense, useEffect, useState } from "react";
import { Switch, Route, useLocation, withRouter } from "react-router-dom";
import { Helmet } from "react-helmet";
import Navbar from "./container/NavBar";
import Footer from "./component/Footer";
import Loader from "./component/Loader";
import { PopupControl } from "component/PopupControl/PopupControl";
import StrapiAPI from "./api/StrapiAPI";

import { convertNullToUndefined } from "./helpers";

const HomePage = React.lazy(() => import("./container/Home"));
const AboutPage = React.lazy(() => import("./container/About"));
const ThingsToDoPage = React.lazy(() => import("./container/ThingsToDo"));
const GettingHerePage = React.lazy(() => import("./container/GettingHere"));
const PackagesPage = React.lazy(() => import("./container/Packages"));
const DiningPage = React.lazy(() => import("./container/Dining"));
const RoomsPage = React.lazy(() => import("./container/Rooms"));
const InsidersGuidePage = React.lazy(() => import("./container/InsidersGuide"));
const PoliciesPage = React.lazy(() => import("./container/Policies"));
const PressPage = React.lazy(() => import("./container/Press"));
const FAQSection = React.lazy(() => import("./component/FAQSection"));

function App({ history }) {
  const [coreData, setCoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // Fetch only core shared data on app initialization
    const fetchCoreData = async () => {
      try {
        const data = await StrapiAPI.getCoreData();
        setCoreData(convertNullToUndefined(data));
        setLoading(false);
      } catch (error) {
        console.error("Failed to load core data:", error);
        setLoading(false);
      }
    };

    fetchCoreData();

    const unlisten = history.listen(() => {
      window.scrollTo(0, 0);
    });

    return () => {
      unlisten();
    };
  }, [history]);

  if (loading || !coreData) {
    return <Loader></Loader>;
  }
  const { Popup } = coreData;

  // Only allow indexing in production
  const isProduction = process.env.NODE_ENV === "production";
  const robotsContent = isProduction
    ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    : "noindex, nofollow";

  return (
    <Suspense fallback={<Loader></Loader>}>
      <Helmet>
        <meta name="robots" content={robotsContent} />
        <meta name="googlebot" content={robotsContent} />
        <meta name="bingbot" content={robotsContent} />
      </Helmet>
      <div className="App">
        <a className="skip-to-content-link" href="#page-content" tabIndex={0}>
          Skip to content
        </a>
        {Popup && !Popup.Disabled && (
          <PopupControl key={location.pathname} cmsData={Popup}></PopupControl>
        )}
        <Navbar cmsData={coreData} />
        <Switch>
          <Route
            exact
            path="/rooms"
            component={() => <RoomsPage footerData={coreData} />}
          />
          <Route
            exact
            path="/thingstodo"
            component={() => <ThingsToDoPage footerData={coreData} />}
          />
          <Route
            exact
            path="/dining"
            component={() => <DiningPage footerData={coreData} />}
          />
          <Route
            exact
            path="/about"
            component={() => <AboutPage footerData={coreData} />}
          />
          <Route
            exact
            path="/getting-here"
            component={() => <GettingHerePage footerData={coreData} />}
          />
          <Route
            exact
            path="/insider-guide"
            component={() => <InsidersGuidePage footerData={coreData} />}
          />
          <Route
            exact
            path="/press"
            component={() => <PressPage footerData={coreData} />}
          />
          <Route
            exact
            path="/packages"
            component={() => (
              <PackagesPage footerData={coreData} contentType="packages" />
            )}
          />
          <Route
            exact
            path="/policies"
            component={() => <PoliciesPage footerData={coreData} type="main" />}
          />
          <Route
            exact
            path="/faq"
            component={() => <FAQSection footerData={coreData} />}
          />
          <Route
            exact
            path="/health-policies"
            component={() => (
              <PoliciesPage footerData={coreData} type="health" />
            )}
          />
          <Route
            exact
            path="/last-minute-specials"
            component={() => (
              <PackagesPage
                footerData={coreData}
                contentType="lms"
                heading="Last Minute Specials"
              />
            )}
          />
          <Route
            path="/"
            component={() => <HomePage footerData={coreData} />}
          />
        </Switch>
        <Footer cmsData={coreData} />
        {/* {
          <FavouriteManager
            CoreData={coreData}
            Rooms={AmpRooms}
            LMS={AmpLMS}
            Packages={AmpPackages}
          />
        } */}
      </div>
    </Suspense>
  );
}

export default withRouter(App);
